import { useCallback, type MutableRefObject, type RefObject } from 'react'
import type { default as DataGrid } from 'devextreme-react/data-grid'

export type UseModelDuplicateGridParams = {
	gridRef: RefObject<DataGrid<any, any> | null>
	selectedRowRef: MutableRefObject<any>
	editorMode: boolean
	duplicateFields: string[] | null
	key: any
	duplicateInitRef: MutableRefObject<Record<string, unknown> | null>
}

export function useModelDuplicateGrid({
	gridRef,
	selectedRowRef,
	editorMode,
	duplicateFields,
	key,
	duplicateInitRef,
}: UseModelDuplicateGridParams) {
	return useCallback(() => {
		const inst = gridRef.current?.instance
		const src = selectedRowRef.current
		if (!inst || !src) return
		if (editorMode) inst.closeEditCell?.()

		let newData: Record<string, unknown>
		if (duplicateFields && duplicateFields.length > 0) {
			newData = {}
			duplicateFields.forEach(f => {
				if (Object.prototype.hasOwnProperty.call(src, f)) {
					newData[f] = (src as Record<string, unknown>)[f]
				}
			})
		} else {
			newData = { ...(src as Record<string, unknown>) }
		}
		if (Array.isArray(key)) {
			key.forEach((k: string) => delete newData[k])
		} else if (key != null) {
			delete newData[key as string]
		}

		const scrollTop = () => {
			const g = gridRef.current?.instance
			if (!g) return
			const s = g.getScrollable?.()
			if (!s) return
			if (!editorMode) {
				s.scrollTo(0)
				return
			}
			g.updateDimensions?.()
			const apply = () => {
				s.scrollTo({ top: 0, left: 0 })
				requestAnimationFrame(() => {
					const g2 = gridRef.current?.instance
					const s2 = g2?.getScrollable?.()
					const rowEl = g2?.getRowElement?.(0)
					if (s2 && rowEl?.[0]) {
						try {
							s2.scrollToElement(rowEl[0])
						} catch {
							s2.scrollTo({ top: 0, left: 0 })
						}
					}
				})
			}
			const upd = s.update?.()
			if (upd != null && typeof (upd as PromiseLike<void>).then === 'function') {
				Promise.resolve(upd).then(apply).catch(apply)
			} else {
				apply()
			}
		}

		const insert = () => {
			const g = gridRef.current?.instance
			if (!g) return
			duplicateInitRef.current = newData
			g.addRow()
			scrollTop()
			requestAnimationFrame(() => {
				scrollTop()
				setTimeout(() => {
					scrollTop()
					if (editorMode) setTimeout(scrollTop, 100)
				}, 0)
			})
		}

		const run = () => {
			scrollTop()
			setTimeout(insert, editorMode ? 250 : 100)
		}

		if (editorMode) {
			requestAnimationFrame(() => requestAnimationFrame(() => setTimeout(run, 0)))
		} else {
			run()
		}
	}, [editorMode, duplicateFields, key, duplicateInitRef])
}
